import { useState, useEffect, useCallback, useRef } from 'react';
import { locationManager } from '@telegram-apps/sdk';
import { apiClient } from '../../api/client';
import { YandexMetrika } from '../../utils/yandexMetrika';
interface UserLocationState {
    position: [number, number] | null;
    accuracy: number;
    heading: number | null;
    speed: number | null;
    altitude: number | null;
    hasAccess: boolean;
    error: string | null;
    isLoading: boolean;
}

interface UseUserLocationOptions {
    loggingInterval?: number; // Интервал логирования в мс (по умолчанию 5000 = 5 сек)
    onAccessDenied?: () => void;
}

export const useUserLocation = (options?: UseUserLocationOptions) => {
    const { loggingInterval = 5000, onAccessDenied } = options || {};
    const [state, setState] = useState<UserLocationState>({
        position: null,
        accuracy: 0,
        heading: null,
        speed: null,
        altitude: null,
        hasAccess: false,
        error: null,
        isLoading: false,
    });

    const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(false);
    const hasAskedOnceRef = useRef(false); // 🔥 Флаг первого автоматического запроса
    const lastLoggedTimeRef = useRef<number>(0);

    // 🔹 Функция обновления позиции
    const updatePosition = useCallback(async () => {
        if (!locationManager.requestLocation.isAvailable()) {
            return null;
        }

        try {
            const location = await locationManager.requestLocation();

            if (location) {
                setState(prev => ({
                    ...prev,
                    position: [location.longitude, location.latitude],
                    accuracy: location.horizontal_accuracy || 0,
                    heading: location.course ?? null,
                    speed: location.speed ?? null,
                    altitude: location.altitude ?? null,
                    hasAccess: true,
                    error: null,
                }));

                const now = Date.now();
                const timeSinceLastLog = now - lastLoggedTimeRef.current;

                if (timeSinceLastLog >= loggingInterval) {
                    lastLoggedTimeRef.current = now;

                    // Отправляем в фоне, игнорируем ошибки
                    apiClient.logLocation(location.latitude, location.longitude).catch(() => { });
                }

                return [location.longitude, location.latitude] as [number, number];
            }

            return null;
        } catch (error) {
            console.warn('⚠️ Ошибка обновления позиции:', error);
            return null;
        }
    }, [loggingInterval]);

    // 🔹 Запуск периодических обновлений (каждую секунду)
    const startUpdates = useCallback(() => {
        if (updateIntervalRef.current) {
            console.log('🔄 Обновления уже запущены');
            return;
        }

        console.log('▶️ Запускаем обновления геолокации (каждую секунду)');
        updateIntervalRef.current = setInterval(updatePosition, 1000); // 🔥 Каждую секунду
    }, [updatePosition]);

    // 🔹 Остановка обновлений
    const stopUpdates = useCallback(() => {
        if (updateIntervalRef.current) {
            console.log('⏸️ Останавливаем обновления');
            clearInterval(updateIntervalRef.current);
            updateIntervalRef.current = null;
        }
    }, []);

    // 🔹 Инициализация + ПЕРВЫЙ автоматический запрос
    useEffect(() => {
        const init = async () => {
            // Проверка поддержки
            if (!locationManager.isSupported()) {
                console.error('❌ Location Manager не поддерживается');
                setState(prev => ({
                    ...prev,
                    error: 'Геолокация не поддерживается',
                }));
                return;
            }

            // Монтирование
            if (!locationManager.mount.isAvailable()) {
                console.error('❌ Монтирование недоступно');
                return;
            }

            try {
                console.log('📱 Монтируем Location Manager...');
                await locationManager.mount();
                isMountedRef.current = true;
                console.log('✅ Location Manager смонтирован');

                // 🔥 ПЕРВЫЙ АВТОМАТИЧЕСКИЙ ЗАПРОС (только один раз!)
                if (!hasAskedOnceRef.current && locationManager.requestLocation.isAvailable()) {
                    hasAskedOnceRef.current = true; // ✅ Помечаем что спросили
                    console.log('🎯 Первый автоматический запрос геолокации...');

                    try {
                        const location = await locationManager.requestLocation();

                        if (location) {
                            console.log('✅ Разрешение получено автоматически');
                            setState({
                                position: [location.longitude, location.latitude],
                                accuracy: location.horizontal_accuracy || 0,
                                heading: location.course ?? null,
                                speed: location.speed ?? null,
                                altitude: location.altitude ?? null,
                                hasAccess: true,
                                error: null,
                                isLoading: false,
                            });

                            lastLoggedTimeRef.current = Date.now();
                            apiClient.logLocation(location.latitude, location.longitude).catch(() => { });

                            // ✅ Запускаем обновления
                            startUpdates();
                        } else {
                            console.log('❌ Координаты не получены');
                            setState(prev => ({
                                ...prev,
                                hasAccess: false,
                                error: 'Координаты не получены',
                            }));
                        }
                    } catch (error) {
                        console.error('❌ Пользователь отказал в доступе');
                        setState(prev => ({
                            ...prev,
                            hasAccess: false,
                            error: 'Доступ к геолокации не предоставлен',
                        }));
                        // ❌ НЕ запускаем обновления, если отказал
                    }
                }
            } catch (error) {
                console.error('❌ Ошибка монтирования:', error);
                setState(prev => ({
                    ...prev,
                    error: 'Ошибка инициализации геолокации',
                }));
            }
        };

        init();

        return () => {
            stopUpdates();
            if (isMountedRef.current && locationManager.unmount) {
                locationManager.unmount();
                isMountedRef.current = false;
            }
        };
    }, []); // ✅ Пустой массив - выполнится ОДИН РАЗ

    // 🔹 Запрос геолокации по кнопке (можно спрашивать повторно)
    const centerOnUser = useCallback(async () => {
        console.log('🎯 Запрос геолокации (по кнопке)');

        setState(prev => ({ ...prev, isLoading: true }));

        if (!locationManager.requestLocation.isAvailable()) {
            console.error('❌ requestLocation недоступен');
            setState(prev => ({
                ...prev,
                error: 'Геолокация недоступна',
                isLoading: false,
            }));
            onAccessDenied?.();
            return null;
        }

        try {
            const location = await locationManager.requestLocation();

            if (location) {
                console.log('✅ Геолокация получена по кнопке');
                YandexMetrika.setMyLocationOn();

                const position: [number, number] = [location.longitude, location.latitude];

                setState({
                    position: position,
                    accuracy: location.horizontal_accuracy || 0,
                    heading: location.course ?? null,
                    speed: location.speed ?? null,
                    altitude: location.altitude ?? null,
                    hasAccess: true,
                    error: null,
                    isLoading: false,
                });

                // ✅ Запускаем обновления (если еще не запущены)
                if (!updateIntervalRef.current) {
                    startUpdates();
                }

                return position;
            } else {
                console.log('⛔ Локация не получена');
                setState(prev => ({
                    ...prev,
                    error: 'Координаты не получены',
                    isLoading: false,
                }));
                onAccessDenied?.();
                return null;
            }
        } catch (error) {
            console.error('❌ Отказано в доступе к геолокации (по кнопке):', error);

            // 🔥 Если доступ не получен - предлагаем открыть настройки
            // if (locationManager.openSettings && locationManager.openSettings.isAvailable()) {
            //     console.log('📱 Открываем настройки геолокации...');
            //     try {
            //         locationManager.openSettings();
            //     } catch (settingsError) {
            //         console.warn('⚠️ Не удалось открыть настройки:', settingsError);
            //     }
            // }

            setState(prev => ({
                ...prev,
                hasAccess: false,
                error: 'Доступ к геолокации не предоставлен',
                isLoading: false,
            }));
            onAccessDenied?.();
            return null;
        }
    }, [startUpdates, onAccessDenied]);

    return {
        position: state.position,
        accuracy: state.accuracy,
        heading: state.heading,
        speed: state.speed,
        altitude: state.altitude,
        hasAccess: state.hasAccess,
        error: state.error,
        isLoading: state.isLoading,
        centerOnUser,
    };
};